import React from 'react';
import { Shield, Car } from 'lucide-react';
import type { WarrantyFormulaStats } from '../types/dashboard.types';

interface GuaranteeFormulaCardProps {
  formula: WarrantyFormulaStats;
  showRevenue?: boolean;
  isTransferPriceMode?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatKm = (value: number) =>
  new Intl.NumberFormat('fr-FR').format(value) + ' km';

export const GuaranteeFormulaCard: React.FC<GuaranteeFormulaCardProps> = ({
  formula,
  showRevenue = true,
  isTransferPriceMode = false,
}) => {
  return (
    <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-5 hover:tw-shadow-md tw-transition-shadow">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h4 className="tw-text-base tw-font-semibold tw-text-gray-900">
          {formula.formulaName}
        </h4>
        <span className="tw-text-xs tw-font-medium tw-bg-affinicar-lighter tw-text-affinicar-primary tw-px-2 tw-py-1 tw-rounded-full">
          {formula.duration} mois
        </span>
      </div>

      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
        {/* Côté garantie */}
        <div>
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
            <Shield className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
            <span className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">
              Garantie
            </span>
          </div>
          <div className="tw-space-y-2">
            <div>
              <p className="tw-text-xs tw-text-gray-500">Volume souscrit</p>
              <p className="tw-text-sm tw-font-bold tw-text-gray-900">
                {formula.volume}
                <span className="tw-text-xs tw-font-normal tw-text-green-600 tw-ml-1">
                  ({formula.soldPercent}% vendus)
                </span>
              </p>
            </div>
            {showRevenue && !isTransferPriceMode && (
              <div>
                <p className="tw-text-xs tw-text-gray-500">Prix moyen</p>
                <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                  {formatCurrency(formula.averagePrice)}
                </p>
              </div>
            )}
            {showRevenue && (
              <div>
                <p className="tw-text-xs tw-text-gray-500">{isTransferPriceMode ? 'Rétro. totale' : 'CA total'}</p>
                <p className="tw-text-sm tw-font-semibold tw-text-green-700">
                  {formatCurrency(isTransferPriceMode ? formula.transferPriceTotal : formula.revenue)}
                </p>
              </div>
            )}
            {showRevenue && isTransferPriceMode && (
              <div>
                <p className="tw-text-xs tw-text-gray-500">Refact. totale</p>
                <p className="tw-text-sm tw-font-semibold tw-text-orange-700">
                  {formatCurrency(formula.memberTransferPriceTotal)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Côté véhicule */}
        <div>
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
            <Car className="tw-w-4 tw-h-4 tw-text-orange-600" />
            <span className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">
              Véhicule couvert
            </span>
          </div>
          <div className="tw-space-y-2">
            <div>
              <p className="tw-text-xs tw-text-gray-500">KM moyen</p>
              <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                {formula.averageKm > 0 ? formatKm(formula.averageKm) : '—'}
              </p>
            </div>
            <div>
              <p className="tw-text-xs tw-text-gray-500">Âge moyen</p>
              <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                {formula.averageVehicleAge > 0
                  ? `${formula.averageVehicleAge} ans`
                  : '—'}
              </p>
            </div>
            <div>
              <p className="tw-text-xs tw-text-gray-500">Prix moyen véhicule</p>
              <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                {formula.averageVehiclePrice > 0
                  ? formatCurrency(formula.averageVehiclePrice)
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
